import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, MapPin, Video, Clock, CheckCircle2, Info, ChevronLeft, ChevronRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import {
  formatSlotDate,
  formatSlotTime,
  dayKey,
  type AppointmentMode,
  type SlotStatus,
} from "@/lib/appointments";

interface Slot {
  id: string;
  starts_at: string;
  duration_minutes: number;
  mode: AppointmentMode;
  status: SlotStatus;
}

const Termin = () => {
  useSeo({
    title: "Termin buchen | Praxis Innere Balance Wien",
    description:
      "Buchen Sie Erst- und Folgetermine vor Ort in der Praxis Innere Balance in Wien online. Zoom-Termine sind nach persönlicher Vereinbarung möglich. Ersttermine finden grundsätzlich in der Praxis statt.",



    keywords: "Termin buchen Wien, Komplementärtherapie Termin, Onlinetermin Praxis IB",
    canonical: "/termin",
  });

  const [allSlots, setAllSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [appointmentType, setAppointmentType] = useState<"Ersttermin" | "Folgetermin">("Ersttermin");

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("appointment_slots")
        .select("id,starts_at,duration_minutes,mode,status")
        .eq("mode", "praxis")
        .eq("status", "open")
        .gt("starts_at", new Date().toISOString())
        .order("starts_at", { ascending: true });
      if (error) toast.error("Termine konnten nicht geladen werden.");
      setAllSlots((data as Slot[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const visibleSlots = useMemo(
    () => allSlots.filter((s) => s.mode === "praxis" && s.status === "open"),
    [allSlots],
  );

  const slots = useMemo(
    () => visibleSlots.filter((s) => s.status === "open"),
    [visibleSlots],
  );



  const byDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const k = dayKey(s.starts_at);
      map.set(k, [...(map.get(k) ?? []), s]);
    }
    return map;
  }, [slots]);

  const visibleByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of visibleSlots) {
      const k = dayKey(s.starts_at);
      map.set(k, [...(map.get(k) ?? []), s]);
    }
    return map;
  }, [visibleSlots]);

  const startOfWeek = (d: Date) => {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
    return x;
  };

  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [activeDay, setActiveDay] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"week" | "month">("week");

  // Beim Laden auf die erste Woche mit einem sichtbaren Terminfenster springen.
  useEffect(() => {
    const firstVisible = slots[0] ?? visibleSlots[0];
    if (!firstVisible) return;
    const first = new Date(firstVisible.starts_at);
    setWeekStart(startOfWeek(first));
    setActiveDay(dayKey(firstVisible.starts_at));
  }, [slots, visibleSlots]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat("de-AT", { month: "long", year: "numeric" }).format(weekStart),
    [weekStart],
  );

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const f = new Intl.DateTimeFormat("de-AT", { day: "2-digit", month: "2-digit" });
    return `${f.format(weekStart)} – ${f.format(end)}`;
  }, [weekStart]);

  const canGoBack = useMemo(() => {
    const today = new Date();
    if (viewMode === "month") {
      return (
        new Date(weekStart.getFullYear(), weekStart.getMonth(), 1) >
        new Date(today.getFullYear(), today.getMonth(), 1)
      );
    }
    return weekStart > startOfWeek(today);
  }, [weekStart, viewMode]);

  const keyOf = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Beim Durchklicken der Wochen-/Monatsansicht läuft die Terminanzeige unten mit:
  // Es wird automatisch der erste Tag mit freien Terminen der sichtbaren Ansicht gewählt.
  useEffect(() => {
    if (loading || visibleSlots.length === 0) return;
    const rangeStart = keyOf(weekStart);
    let rangeEnd: string;
    let monthPrefix: string | null = null;
    if (viewMode === "week") {
      const e = new Date(weekStart);
      e.setDate(e.getDate() + 6);
      rangeEnd = keyOf(e);
    } else {
      rangeEnd = "9999-12-31";
      monthPrefix = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}`;
    }
    const inView = (k: string) =>
      k >= rangeStart && k <= rangeEnd && (monthPrefix ? k.startsWith(monthPrefix) : true);
    // Gewählten Tag behalten, solange er in der Ansicht liegt und Terminfenster hat.
    if (activeDay && inView(activeDay) && (visibleByDay.get(activeDay)?.length ?? 0) > 0) return;
    const first = slots.find((s) => inView(dayKey(s.starts_at)))
      ?? visibleSlots.find((s) => inView(dayKey(s.starts_at)));
    setActiveDay(first ? dayKey(first.starts_at) : null);
  }, [weekStart, viewMode, slots, visibleSlots, activeDay, visibleByDay, loading]);

  const weekCells = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      const key = keyOf(d);
      return {
        key,
        day: d.getDate(),
        count: byDay.get(key)?.length ?? 0,
        total: visibleByDay.get(key)?.length ?? 0,
        outside: false,
      };
    });
  }, [weekStart, byDay, visibleByDay]);

  const monthCells = useMemo(() => {
    const monthIndex = weekStart.getMonth();
    const first = new Date(weekStart.getFullYear(), monthIndex, 1);
    const last = new Date(weekStart.getFullYear(), monthIndex + 1, 0);
    const gridStart = startOfWeek(first);
    const gridEnd = startOfWeek(last);
    gridEnd.setDate(gridEnd.getDate() + 6);
    const cells: { key: string; day: number; count: number; total: number; outside: boolean }[] = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      const key = keyOf(cursor);
      const outside = cursor.getMonth() !== monthIndex;
      cells.push({
        key,
        day: cursor.getDate(),
        count: outside ? 0 : (byDay.get(key)?.length ?? 0),
        total: outside ? 0 : (visibleByDay.get(key)?.length ?? 0),
        outside,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return cells;
  }, [weekStart, byDay, visibleByDay]);

  const visibleCells = viewMode === "month" ? monthCells : weekCells;

  const shift = (direction: 1 | -1) =>
    setWeekStart((w) => {
      if (viewMode === "month") {
        const target = new Date(w.getFullYear(), w.getMonth() + direction, 1);
        const today = new Date();
        if (
          direction === -1 &&
          target.getFullYear() === today.getFullYear() &&
          target.getMonth() === today.getMonth()
        ) {
          return startOfWeek(today);
        }
        return target;
      }
      const n = new Date(w);
      n.setDate(n.getDate() + direction * 7);
      return n;
    });


  const monthSlotCount = useMemo(
    () =>
      slots.filter((s) => {
        const d = new Date(s.starts_at);
        return d.getFullYear() === weekStart.getFullYear() && d.getMonth() === weekStart.getMonth();
      }).length,
    [slots, weekStart],
  );

  const weekSlotCount = useMemo(
    () => weekCells.reduce((sum, c) => sum + c.count, 0),
    [weekCells],
  );

  const activeDaySlots = useMemo(
    () => (activeDay ? (byDay.get(activeDay) ?? []) : []),
    [activeDay, byDay],
  );

  const activeDayVisibleSlots = useMemo(
    () => (activeDay ? (visibleByDay.get(activeDay) ?? []) : []),
    [activeDay, visibleByDay],
  );

  const nextSlot = slots[0] ?? null;

  // Karte folgt beim Durchblättern dem aktiven Tag; Auswahl hat Vorrang.
  const previewSlot = selected ?? activeDaySlots[0] ?? nextSlot;





  // Ist der angezeigte Monat noch gar nicht freigeschaltet (liegt nach dem letzten Termin)?
  const monthNotReleased = useMemo(() => {
    if (visibleSlots.length === 0) return true;
    const last = new Date(visibleSlots[visibleSlots.length - 1].starts_at);
    return (
      new Date(weekStart.getFullYear(), weekStart.getMonth(), 1) >
      new Date(last.getFullYear(), last.getMonth(), 1)
    );
  }, [weekStart, visibleSlots]);



  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selected) return;
    if (!appointmentType) {
      toast.error("Bitte wählen Sie aus, ob es sich um einen Ersttermin oder Folgetermin handelt.");
      return;
    }

    if (!accepted) {
      toast.error("Bitte stimmen Sie der Datenverarbeitung gemäß DSGVO zu, damit wir Ihre Anfrage senden können.");
      return;
    }
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);

    try {
      const { data, error } = await supabase.functions.invoke("book-appointment", {
          body: {
            slotId: selected.id,
            firstName: fd.get("firstName"),
            lastName: fd.get("lastName"),
            street: fd.get("street"),
            postalCode: fd.get("postalCode"),
            city: fd.get("city"),
            phone: fd.get("phone"),
            email: fd.get("email"),
            concern: `Terminart: ${appointmentType}${fd.get("concern") ? `\n\n${fd.get("concern")}` : ""}`,
            firstVisitConfirmed: true,
          },
      });
      if (error) throw error;
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      setDone(true);
      setDialogOpen(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Buchung fehlgeschlagen:", err);
      toast.error(
        "Leider konnte die Anfrage nicht abgeschlossen werden. Bitte versuchen Sie es noch einmal oder rufen Sie mich kurz an: 0660 404 1993.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="text-primary font-medium text-sm uppercase tracking-wider">
              Online-Terminbuchung
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
              Termin buchen
            </h1>
            <p className="text-muted-foreground">
              Hier buchen Sie Erst- und Folgetermine vor Ort in der Praxis. Ersttermine sollten
              grundsätzlich persönlich in der Praxis stattfinden. Ist Ihnen das nicht möglich,
              sprechen Sie mich gerne an – gemeinsam besprechen wir eine passende Lösung.
            </p>
            <p className="text-sm text-muted-foreground mt-3">
              Zoom-Termine sind weiterhin möglich und werden individuell direkt mit mir
              vereinbart – per{" "}
              <a href="mailto:office@ibpraxis.net" className="text-primary hover:underline">E-Mail</a>
              {" "}oder{" "}
              <a href="tel:+436604041993" className="text-primary hover:underline">Telefon</a>.
            </p>
          </div>


          {done ? (
            <Card className="max-w-2xl mx-auto p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-3">
                Vielen Dank für Ihre Buchung
              </h2>
              <p className="text-muted-foreground mb-4">
                Ihr Termin
                {selected && (
                  <>
                    {" "}
                    am <strong>{formatSlotDate(selected.starts_at)}</strong> um{" "}
                    <strong>{formatSlotTime(selected.starts_at)}</strong>
                  </>
                )}{" "}
                ist verbindlich für Sie reserviert.
              </p>
              <p className="text-muted-foreground mb-4">
                Innerhalb von 24 Stunden erhalten Sie von mir persönlich eine schriftliche
                Bestätigung per E-Mail. Ihr Termin gilt bereits jetzt als gesichert – die
                Bestätigung dient lediglich Ihrer Unterlage.
              </p>
              <p className="text-muted-foreground">
                Ich freue mich auf unser Gespräch und darauf, gemeinsam mit Ihnen an
                Ihrer Gesundheit zu arbeiten.
              </p>
            </Card>
          ) : (
            <>
            <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-8">

              {/* Slot-Auswahl */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-foreground flex items-center gap-2 mb-3">
                    <CalendarDays className="w-5 h-5 text-primary" />
                    Erst- & Folgetermine vor Ort
                  </h2>
                </div>






                {!loading && previewSlot && (
                  <Card className="mb-4 border-primary/30 bg-primary/5 p-4">
                    <p className="text-xs uppercase tracking-wider text-primary font-medium mb-1">
                      {selected ? "Ausgewählter Termin" : "Nächster freier Termin"}
                    </p>
                    <p className="font-medium text-foreground">
                      {formatSlotDate(previewSlot.starts_at)}
                    </p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <Clock className="w-4 h-4" />
                      {formatSlotTime(previewSlot.starts_at)} · ca.{" "}
                      {previewSlot.duration_minutes} Min.
                    </p>
                    <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                      <MapPin className="w-3.5 h-3.5" />
                      Praxis vor Ort
                    </p>

                    {selected ? (
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(null);
                          if (nextSlot) {
                            setWeekStart(startOfWeek(new Date(nextSlot.starts_at)));
                            setActiveDay(dayKey(nextSlot.starts_at));
                          }
                        }}
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Zurück zum nächstfreien Termin
                      </button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        className="mt-3 w-full sm:w-auto"
                        onClick={() => {
                          setWeekStart(startOfWeek(new Date(previewSlot.starts_at)));
                          setActiveDay(dayKey(previewSlot.starts_at));
                          setSelected(previewSlot);
                        }}
                      >
                        Diesen Termin wählen
                      </Button>
                    )}
                  </Card>
                )}


                {loading ? (
                  <p className="text-muted-foreground">Termine werden geladen …</p>
                ) : visibleSlots.length === 0 ? (
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      Aktuell sind keine Online-Termine freigeschaltet. Gerne können Sie mir direkt
                      eine Nachricht schreiben oder anrufen: 0660 404 1993 oder
                      office@ibpraxis.net.
                    </p>
                  </Card>
                ) : (
                  <Card className="p-4 md:p-5">
                    {/* Umschalter Woche / Monat */}
                    <div className="flex justify-center mb-3">
                      <div className="inline-flex rounded-lg border border-border p-0.5">
                        {(["week", "month"] as const).map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setViewMode(m)}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              viewMode === m
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {m === "week" ? "Woche" : "Monat"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between mb-3">
                      <button
                        type="button"
                        onClick={() => shift(-1)}
                        disabled={!canGoBack}
                        className="p-2 rounded-lg border border-border text-foreground hover:border-primary disabled:opacity-40 disabled:hover:border-border"
                        aria-label={viewMode === "month" ? "Vorheriger Monat" : "Vorherige Woche"}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="text-center">
                        <p className="font-medium text-foreground">{monthLabel}</p>
                        {viewMode === "week" && (
                          <p className="text-xs text-muted-foreground">Woche {weekLabel}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => shift(1)}
                        className="p-2 rounded-lg border border-border text-foreground hover:border-primary"
                        aria-label={viewMode === "month" ? "Nächster Monat" : "Nächste Woche"}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>

                    {monthSlotCount === 0 && (
                      <p className="text-sm mb-4 rounded-lg bg-muted/60 px-3 py-2 text-muted-foreground">
                        {monthNotReleased
                          ? `Die Termine für ${monthLabel} werden noch freigeschaltet. Sobald die Planung abgeschlossen ist, erscheinen sie an dieser Stelle.`
                          : `Für ${monthLabel} sind derzeit keine freien Termine verfügbar.`}
                      </p>
                    )}


                    <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-1">
                      {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => (
                        <span key={d}>{d}</span>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {visibleCells.map((cell) => (
                          <button
                            key={cell.key}
                            type="button"
                            disabled={cell.total === 0}
                            onClick={() => setActiveDay(cell.key)}
                            className={`aspect-square rounded-lg text-sm flex flex-col items-center justify-center transition-colors ${
                              activeDay === cell.key
                                ? "bg-primary text-primary-foreground"
                                : cell.count > 0
                                  ? "bg-primary/10 text-foreground hover:bg-primary/20 font-medium"
                                  : cell.total > 0
                                    ? "bg-muted/70 text-muted-foreground hover:bg-muted font-medium"
                                  : cell.outside
                                    ? "text-muted-foreground/30"
                                    : "text-muted-foreground/50"
                            }`}
                          >
                            {cell.day}
                            {cell.count > 0 && (
                              <span
                                className={`w-1.5 h-1.5 rounded-full mt-0.5 ${
                                  activeDay === cell.key ? "bg-primary-foreground" : "bg-primary"
                                }`}
                              />
                            )}
                          </button>
                      ))}
                    </div>
                    {viewMode === "week" && weekSlotCount === 0 && monthSlotCount > 0 && (
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        In dieser Woche sind keine Termine frei – bitte blättern Sie weiter.
                      </p>
                    )}


                    <div className="mt-5 border-t border-border pt-4">
                      {activeDayVisibleSlots.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          In dieser Ansicht sind derzeit keine freien Termine – bitte blättern Sie
                          weiter oder wählen Sie einen hervorgehobenen Tag.
                        </p>
                      ) : (
                        <>
                          <p className="font-medium text-foreground mb-2">
                            {formatSlotDate(activeDayVisibleSlots[0].starts_at)}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {activeDayVisibleSlots.map((s) => (
                              <Button
                                key={s.id}
                                type="button"
                                variant={selected?.id === s.id ? "default" : "outline"}
                                onClick={() => setSelected(s)}
                                className="gap-2"
                              >
                                <MapPin className="w-4 h-4" />
                                {formatSlotTime(s.starts_at)}
                              </Button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                )}

                <div className="mt-6 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>
                      <strong className="text-foreground">Kein passender Termin dabei?</strong> Die
                      hier angezeigten Zeitfenster bilden nur einen Teil meiner Planung ab; weitere
                      Termine schalte ich laufend frei. Schreiben Sie mir in diesem Fall gerne
                      direkt – in den allermeisten Fällen findet sich gemeinsam eine gute Lösung,
                      auch kurzfristig oder außerhalb der angezeigten Zeiten. Sie erreichen mich
                      telefonisch unter{" "}
                      <a href="tel:+436604041993" className="text-primary hover:underline">
                        0660 404 1993
                      </a>{" "}
                      oder per E-Mail an{" "}
                      <a href="mailto:office@ibpraxis.net" className="text-primary hover:underline">
                        office@ibpraxis.net
                      </a>
                      .
                    </span>
                  </p>
                </div>


                <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>
                      <strong className="text-foreground">Stornobedingungen:</strong> Ich freue mich
                      auf unser Gespräch. Sollten Sie doch einmal verhindert sein, bitten wir um eine
                      kurze Nachricht bis 24 Stunden vor dem Termin – telefonisch unter 0660 404 1993
                      oder per E-Mail an office@ibpraxis.net. Bei späterer Absage oder
                      unentschuldigtem Nicht-Erscheinen wird das vereinbarte Honorar in Rechnung
                      gestellt.
                    </span>
                  </p>
                </div>
              </div>

              {/* Formular */}
              <Card className="p-6 md:p-8 h-fit">
                {!selected ? (
                  <p className="text-muted-foreground">
                    Wählen Sie bitte zuerst einen freien Tag und einen passenden Termin aus dem
                    Kalender aus.
                  </p>
                ) : (
                  <>
                    <div className="mb-6 rounded-lg bg-muted/50 p-4">
                      <p className="font-medium text-foreground">
                        {formatSlotDate(selected.starts_at)}
                      </p>
                      <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatSlotTime(selected.starts_at)} · ca. {selected.duration_minutes} Min.
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-4 h-4" /> Praxis, Linzer Straße 383/Top 7
                        </span>
                      </p>
                    </div>

                    <form className="space-y-5" onSubmit={handleSubmit}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">Vorname *</Label>
                          <Input id="firstName" name="firstName" required maxLength={100} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Nachname *</Label>
                          <Input id="lastName" name="lastName" required maxLength={100} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="street">Straße und Hausnummer *</Label>
                        <Input id="street" name="street" required maxLength={200} />
                      </div>

                      <div className="grid sm:grid-cols-[8rem_1fr] gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="postalCode">PLZ *</Label>
                          <Input id="postalCode" name="postalCode" required maxLength={20} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city">Ort *</Label>
                          <Input id="city" name="city" required maxLength={100} />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Telefon *</Label>
                          <Input id="phone" name="phone" type="tel" required maxLength={50} />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">E-Mail *</Label>
                          <Input id="email" name="email" type="email" required maxLength={255} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="concern">Ihr Anliegen (optional)</Label>
                        <Textarea
                          id="concern"
                          name="concern"
                          rows={4}
                          maxLength={3000}
                          placeholder="Wenn Sie möchten, erzählen Sie mir hier kurz, was Sie zu mir führt – das hilft mir, Ihren Termin bestmöglich vorzubereiten."
                        />
                      </div>
                      <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                        <Label>Terminart *</Label>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {(["Ersttermin", "Folgetermin"] as const).map((t) => (
                            <label key={t} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="appointmentType"
                                value={t}
                                checked={appointmentType === t}
                                onChange={() => setAppointmentType(t)}
                                className="accent-primary"
                              />
                              <span>{t}</span>
                            </label>
                          ))}
                        </div>
                      </div>


                      <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
                        <Checkbox
                          checked={accepted}
                          onCheckedChange={(v) => setAccepted(v === true)}
                          className="mt-0.5"
                        />
                        <span>
                          Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Terminanfrage
                          gemäß DSGVO verarbeitet und gespeichert werden (Details in der
                          Datenschutzerklärung im Fußbereich dieser Seite). *
                        </span>


                      </label>

                      <Button
                        type="submit"
                        className="w-full"
                        disabled={submitting || !accepted}
                      >
                        {submitting ? "Wird gesendet …" : "Termin anfragen"}

                      </Button>
                    </form>
                  </>
                )}
              </Card>
            </div>

            {/* So funktionieren die Termine */}
            <div className="max-w-5xl mx-auto mt-10 grid md:grid-cols-3 gap-5">
              <Card className="p-5">
                <Video className="w-6 h-6 text-primary mb-3" />
                <h3 className="font-semibold text-foreground mb-2">
                  Wie funktioniert ein Zoom-Termin?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Zoom-Termine sind weiterhin möglich, werden jedoch nicht über den Kalender
                  gebucht. Bitte kontaktieren Sie mich persönlich per{" "}
                  <a href="mailto:office@ibpraxis.net" className="text-primary hover:underline">E-Mail</a>
                  {" "}oder{" "}
                  <a href="tel:+436604041993" className="text-primary hover:underline">Telefon</a>.
                  {" "}Wir besprechen Ihr Anliegen und vereinbaren individuell einen passenden Termin.
                </p>
              </Card>
              <Card className="p-5">
                <MapPin className="w-6 h-6 text-primary mb-3" />
                <h3 className="font-semibold text-foreground mb-2">
                  Wie funktioniert ein Praxis-Termin?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Erst- und Folgetermine können Sie direkt im Kalender buchen. Zum gebuchten Zeitraum sehen wir uns in der Praxis. Hier kann auch bequem mit
                  Bankomat bezahlt werden.
                </p>
              </Card>
              <Card className="p-5">
                <Info className="w-6 h-6 text-primary mb-3" />
                <h3 className="font-semibold text-foreground mb-2">
                  Wo findet der Ersttermin statt?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Ein Ersttermin sollte grundsätzlich vor Ort in der Praxis stattfinden.
                  So können wir uns persönlich kennenlernen und vorhandene Befunde gemeinsam
                  besprechen. Wenn ein Besuch für Sie nicht möglich ist, melden Sie sich bitte
                  bei mir – wir besprechen gemeinsam, ob ein Zoom-Termin für Ihre Situation infrage kommt.
                </p>
              </Card>
            </div>
            </>
          )}
        </div>


      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="mx-auto mb-2">
              <CheckCircle2 className="w-10 h-10 text-primary" />
            </div>
            <DialogTitle className="text-center text-xl">
              Vielen Dank für Ihre Buchung
            </DialogTitle>
            <DialogDescription className="text-center space-y-3 pt-2 text-sm">
              <span className="block">
                Ihr Termin
                {selected && (
                  <>
                    {" "}am <strong>{formatSlotDate(selected.starts_at)}</strong> um{" "}
                    <strong>{formatSlotTime(selected.starts_at)}</strong>
                  </>
                )}{" "}
                ist verbindlich für Sie reserviert.
              </span>
              <span className="block">
                Innerhalb von 24 Stunden melde ich mich persönlich mit einer schriftlichen
                Bestätigung per E-Mail. Ihr Termin gilt bereits jetzt als gesichert.
              </span>
              <span className="block">
                Ich freue mich auf unser Gespräch und darauf, gemeinsam mit Ihnen an
                Ihrer Gesundheit zu arbeiten.
              </span>
            </DialogDescription>
          </DialogHeader>
          <Button className="w-full" onClick={() => setDialogOpen(false)}>
            Schließen
          </Button>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};


export default Termin;
